import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import ToastrService from '@/shared/services/ToastrService';
import intlShape from '@/shared/utils/intlShape';

import api from '@/shared/api';
import handleHttpError from '@/shared/services/HandleHttpError';
import FormattedListValue from '@/shared/components/FormattedListValue';
import FormattedDateShort from '@/shared/components/FormattedDateShort';
import { CanUpdate } from '@/shared/components/security';
import CompoundDaysTimeInput from '@/shared/components/input/CompoundDaysTimeInput';
import YesNoModal from '@/shared/components/YesNoModal';
import { Modal } from 'react-bootstrap';
import ZendeskHelp from '@/shared/components/ZendeskHelp';
import Button from '@/shared/components/Button';
import { displayDaysAndTime, displayHoursAsDaysAndTime, getTimeAbbr } from '@/shared/utils/absenceFormat';
import messages from '../hrmMessages';

export default class AbsenceCorrectionModal extends Component {
  state = {
    rows: [],
    correctionWholeDays: 0,
    correctionRemainderMinutes: 0,
    correctionIsNegative: false,
    remark: '',
    balance: 0,
    saving: false,
    hasChanged: false,
  };

  static propTypes = {
    show: PropTypes.bool.isRequired,
    intl: intlShape.isRequired,
    onClose: PropTypes.func.isRequired,
    hoursInDay: PropTypes.number,
    counter: PropTypes.shape({ id: PropTypes.string }).isRequired,
  };

  static defaultProps = { hoursInDay: 0 };

  reloadData = () => {
    const { counter, intl } = this.props;
    return api.base
      .get(`/api/AbsenceCounter/Details/${counter.id}`)
      .then(({ data }) => {
        const balance = data && data.length > 0 ? data[data.length - 1].balanceAfter : 0;

        this.setState({ rows: data || [], balance });
      })
      .catch(error => handleHttpError(intl)(error));
  };
  componentDidMount() {
    this.reloadData();
  }

  closeModal = () => {
    const { onClose } = this.props;
    const { hasChanged } = this.state;

    onClose(hasChanged);
  };

  addCorrection = () => {
    const { correctionWholeDays, correctionRemainderMinutes, correctionIsNegative, remark } =
      this.state;
    const { counter, intl } = this.props;
    if ((correctionWholeDays ?? 0) === 0 && (correctionRemainderMinutes ?? 0) === 0) return;
    this.setState({ saving: true });
    return api.base
      .post('api/AbsenceCounter/Correction', {
        absenceCounterId: counter.id,
        correctionWholeDays,
        correctionRemainderMinutes,
        correctionIsNegative,
        remark,
      })
      .then(() =>
        this.reloadData().finally(() => {
          ToastrService.success();

          this.setState(
            {
              correctionWholeDays: 0,
              correctionRemainderMinutes: 0,
              correctionIsNegative: false,
              remark: '',
              hasChanged: true,
            },
            this.closeModal,
          );
        }),
      )
      .catch(error => handleHttpError(intl)(error))
      .finally(() => {
        this.setState({ saving: false });
      });
  };

  removeCorrection = id => {
    const { intl } = this.props;
    this.setState({ saving: true });
    return api.base
      .delete(`api/AbsenceCounter/Correction/${id}`)
      .then(() =>
        this.reloadData().finally(() => {
          ToastrService.success();

          this.setState({ hasChanged: true });
        }),
      )
      .catch(error => handleHttpError(intl)(error))
      .finally(() => {
        this.setState({ saving: false, deleteCorrection: null });
      });
  };

  render() {
    const { show, intl, hoursInDay } = this.props;
    const abbr = getTimeAbbr(intl, messages);
    const {
      rows,
      balance,
      correctionWholeDays,
      correctionRemainderMinutes,
      correctionIsNegative,
      remark,
      saving,
      deleteCorrection,
    } = this.state;
    if (!show) return null;

    return (
      <Modal
        enforceFocus={false}
        backdrop="static"
        show={show}
        onHide={this.closeModal}
        dialogClassName="modal-xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {intl.formatMessage(messages.AbsenceCorrections)}{' '}
            <ZendeskHelp article={13956867188764} className="margin5L" />
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <table className="table">
            <thead>
              <tr>
                <th>
                  <FormattedMessage {...messages.Date} />
                </th>
                <th>
                  <FormattedMessage {...messages.Type} />
                </th>
                <th className="text-right">
                  <FormattedMessage {...messages.BalanceBefore} />
                </th>
                <th className="text-right">
                  <FormattedMessage {...messages.Mutation} />
                </th>
                <th className="text-right">
                  <FormattedMessage {...messages.BalanceAfter} />
                </th>
                <th>
                  <FormattedMessage {...messages.Remark} />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows?.map(row => (
                <tr key={row.order}>
                  <td>
                    <FormattedDateShort value={row.date} />
                  </td>
                  <td>
                    <FormattedListValue list="AbsenceDetailType" id={row.type} />
                  </td>
                  <td className="text-right">
                    {(row.balanceBefore ?? 0).toTime()}
                    {hoursInDay ? (
                      <div className="text-muted small">
                        {displayDaysAndTime(row.balanceBeforeWholeDays, row.balanceBeforeRemainderMinutes, abbr)}
                      </div>
                    ) : null}
                  </td>
                  <td className="text-right">
                    {(row.mutation ?? 0).toTime()}
                    {hoursInDay ? (
                      <div className="text-muted small">
                        {displayDaysAndTime(row.mutationWholeDays, row.mutationRemainderMinutes, abbr)}
                      </div>
                    ) : null}
                  </td>
                  <td className="text-right">
                    {(row.balanceAfter ?? 0).toTime()}
                    {hoursInDay ? (
                      <div className="text-muted small">
                        {displayDaysAndTime(row.balanceAfterWholeDays, row.balanceAfterRemainderMinutes, abbr)}
                      </div>
                    ) : null}
                  </td>
                  <td>{row.remark}</td>
                  <td>
                    {row.correctionId ? (
                      <CanUpdate on="AbsenceAndCounters">
                        <a
                          className="btn btn-xs btn-default"
                          title={intl.formatMessage(messages.RemoveCorrection)}
                          onClick={() => this.setState({ deleteCorrection: row.correctionId })}
                        >
                          <i className="fa fa-trash" />
                        </a>
                      </CanUpdate>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
            <CanUpdate on="AbsenceAndCounters">
              <tfoot>
                <tr>
                  <td colSpan={2}>
                    <FormattedMessage {...messages.AddCorrection} />
                  </td>
                  <td className="text-right">
                    {(balance ?? 0).toTime()}
                    {hoursInDay ? (
                      <div className="text-muted small">
                        {displayHoursAsDaysAndTime(balance ?? 0, hoursInDay, abbr)}
                      </div>
                    ) : null}
                  </td>
                  <td>
                    <CompoundDaysTimeInput
                      id="correction"
                      wholeDays={correctionWholeDays}
                      remainderMinutes={correctionRemainderMinutes}
                      onChange={({ wholeDays, remainderMinutes }) =>
                        this.setState({
                          correctionWholeDays: wholeDays,
                          correctionRemainderMinutes: remainderMinutes,
                        })
                      }
                      allowNegative
                      isNegative={correctionIsNegative}
                      onSignChange={v => this.setState({ correctionIsNegative: v })}
                      disabled={saving}
                      showLabels
                      daysLabel={intl.formatMessage(messages.Days)}
                      hoursLabel={intl.formatMessage(messages.Hours)}
                    />
                  </td>
                  <td />
                  <td>
                    <textarea
                      value={remark}
                      className="form-control vertical-resize"
                      disabled={saving}
                      placeholder={intl.formatMessage(messages.Remark)}
                      onChange={e => this.setState({ remark: e.target.value })}
                      maxLength={3000}
                    />
                  </td>
                  <td />
                </tr>
              </tfoot>
            </CanUpdate>
          </table>
          {hoursInDay ? (
            <div>
              <label>{intl.formatMessage(messages.HoursInDay)}:</label>
              &nbsp;
              <span>{hoursInDay.toTime()}</span>
            </div>
          ) : null}

          {deleteCorrection ? (
            <YesNoModal
              intl={intl}
              show
              title={intl.formatMessage(messages.Delete)}
              message={intl.formatMessage(messages.DeleteCorrectionMsg)}
              onNo={() =>
                this.setState({
                  deleteCorrection: null,
                })
              }
              onSave={() => this.removeCorrection(deleteCorrection)}
            />
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button showProgress onClick={this.closeModal}>
            {intl.formatMessage(messages.Close)}
          </Button>
          <CanUpdate on="AbsenceAndCounters">
            <Button
              onClick={this.addCorrection}
              disabled={
                saving ||
                ((correctionWholeDays ?? 0) === 0 && (correctionRemainderMinutes ?? 0) === 0)
              }
              btnClassName="btn-inverse"
              showProgress
              name="OkModal-Ok"
            >
              {intl.formatMessage(messages.Save)}
            </Button>
          </CanUpdate>
        </Modal.Footer>
      </Modal>
    );
  }
}
